import { lsGet, lsSave } from "./localStorage";

// Per-browser navigation preferences for the modern shell (Recent + Favorites).
const RECENT_KEY = "navRecent";
const FAV_KEY = "navFavorites";
const MAX_RECENT = 10;

// Both lists are keyed by PATH, so renaming a screen's route silently orphans
// every shortcut a user had pinned to it — the entry survives in localStorage,
// still renders, and lands on a dead URL. Rewriting the stored path on read is
// the only migration available: this is browser-local data, so there is nothing
// server-side to fix it up.
//
// Add a line here whenever a route is renamed. Entries are cheap and stale ones
// are harmless: a path that no longer appears in anyone's storage simply never
// matches.
const RENAMED_PATHS = {
  // Cash Sales Invoice became Sales Invoice when it learned to sell on credit.
  cash_sales_invoice: "sales_invoice",
};

const migratePath = (path) => {
  if (typeof path !== "string") return path;
  for (const [from, to] of Object.entries(RENAMED_PATHS)) {
    // Anchored at the start so a path is only rewritten when the RENAMED
    // SEGMENT is the route root, never where it happens to appear deeper in.
    if (path === from || path.startsWith(`${from}/`))
      return to + path.slice(from.length);
    if (path === `/${from}` || path.startsWith(`/${from}/`))
      return `/${to}` + path.slice(from.length + 1);
  }
  return path;
};

const migrateList = (key) => {
  const list = lsGet(key) || [];
  let changed = false;
  const next = list.map((x) => {
    const path = migratePath(x?.path);
    if (path !== x?.path) changed = true;
    return path === x?.path ? x : { ...x, path };
  });
  // Written back so the rewrite happens once rather than on every read.
  if (changed) lsSave(key, next);
  return next;
};

export const getRecent = () => migrateList(RECENT_KEY);

export const pushRecent = (item) => {
  if (!item?.path) return;
  const list = (lsGet(RECENT_KEY) || []).filter((x) => x.path !== item.path);
  list.unshift({ title: item.title, path: item.path });
  lsSave(RECENT_KEY, list.slice(0, MAX_RECENT));
};

export const getFavorites = () => migrateList(FAV_KEY);

export const isFavorite = (path) =>
  migrateList(FAV_KEY).some((x) => x.path === path);

export const toggleFavorite = (item) => {
  if (!item?.path) return getFavorites();
  const list = lsGet(FAV_KEY) || [];
  const exists = list.some((x) => x.path === item.path);
  const next = exists
    ? list.filter((x) => x.path !== item.path)
    : [...list, { title: item.title, path: item.path }];
  lsSave(FAV_KEY, next);
  return next;
};
